import { useCallback, useEffect, useRef, useState } from 'react';
import config from '../config.js';
import { closeSocketQuietly } from '../_helpers/webSocketCleanup.js';
import {
  SOCKET_CHAT,
  SocketTicketRefusedError,
  createSocketRetry,
  hasSession,
  mintSocketTicket,
} from '../_helpers/socketTicket.js';
import { useT } from '../contexts/LocaleContext.jsx';
import { englishT } from '../locales/index.js';
import { normalizeNamePaint } from '../_helpers/namePaint.js';

const MAX_HISTORY_MESSAGES = 100;
const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const POLLING_INTERVAL_MS = 10000;
const TYPING_BROADCAST_DEBOUNCE_MS = 1200;
const SEND_RATE_LIMIT_MS = 1500;

const CHAT_ENDPOINT_TEMPLATE = '/bands/{bandId}/chat/messages';
const CHAT_WS_ENDPOINT_TEMPLATE = '/ws/chat/{bandId}';

function getSafeBandId(bandId) {
  const parsedBandId = parseInt(bandId, 10);
  if (!Number.isSafeInteger(parsedBandId) || parsedBandId <= 0) {
    return null;
  }
  return parsedBandId;
}

function getWsBaseUrl() {
  const apiUrl = config.apiBaseUrl || '';
  return apiUrl.replace(/^https/, 'wss').replace(/^http/, 'ws');
}

function getChatHttpUrl(safeBandId) {
  return `${config.apiBaseUrl}${CHAT_ENDPOINT_TEMPLATE.replace('{bandId}', String(safeBandId))}`;
}

// The chat socket is opened with a single-use ticket minted just before the
// connect (ENG-498), never the session JWT.
function getChatWsUrl(safeBandId, ticket) {
  return `${getWsBaseUrl()}${CHAT_WS_ENDPOINT_TEMPLATE.replace('{bandId}', String(safeBandId))}?ticket=${encodeURIComponent(ticket)}`;
}

function getAuthHeaders() {
  const token = sessionStorage.getItem('jwt');
  if (!token) {
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

function getStoredHistoryKey(safeBandId) {
  return `chat-history:${safeBandId}`;
}

// `t` translates the client-side display-name fallback (ENG-386); callers
// without a component `t` get English, as before.
function normalizeChatMessage(input, t = englishT) {
  if (!input || typeof input !== 'object') {
    return null;
  }

  const viewerFallback = t('chat.viewerFallback');
  const id = input.id ?? input.ID ?? input.messageId ?? null;
  const username = String(input.username ?? input.userName ?? input.displayName ?? viewerFallback).trim();
  const message = String(input.message ?? input.text ?? '').trim();
  if (!message) {
    return null;
  }

  const timestamp = input.timestamp ?? input.createdAt ?? new Date().toISOString();
  const normalized = {
    id: id ?? `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    username: username || viewerFallback,
    message,
    timestamp,
  };
  if (input.userId != null) normalized.userId = Number(input.userId);
  // The sender's equipped name paint (ENG-731); omitted when they have none.
  const namePaint = normalizeNamePaint(input.namePaint);
  if (namePaint) normalized.namePaint = namePaint;
  const boost = normalizeChatBoost(input);
  if (boost) {
    normalized.kind = 'boost';
    normalized.boost = boost;
  }
  return normalized;
}

// A boost line's structured fields (ENG-680), so it renders in the viewer's
// language; anything malformed falls back to the plain English message.
function normalizeChatBoost(input) {
  const b = input.kind === 'boost' ? input.boost : null;
  if (!b || typeof b !== 'object' || typeof b.songTitle !== 'string') return null;
  const amountCents = Number(b.amountCents);
  const spot = Number(b.spot);
  if (!Number.isFinite(amountCents) || !Number.isFinite(spot)) return null;
  return {
    entryId: Number(b.entryId) || 0,
    songTitle: b.songTitle,
    amountCents,
    spot,
    passedTitle: typeof b.passedTitle === 'string' ? b.passedTitle : '',
    passedCount: Number(b.passedCount) || 0,
  };
}

function dedupeMessages(items) {
  const byId = new Map();
  items.forEach((item) => {
    if (!item || !item.id) return;
    byId.set(String(item.id), item);
  });
  return Array.from(byId.values()).slice(-MAX_HISTORY_MESSAGES);
}

/**
 * `enabled: false` (the band is not live) leaves the chat idle: the last
 * saved history is shown, but no history request, socket or polling runs,
 * since the backend only has chat for a live band and refuses the history
 * with 400 otherwise (ENG-480). connectionStatus is then 'offline'.
 */
export const useBandChat = ({ bandId, enabled = true }) => {
  const t = useT();
  // Read through a ref so a locale switch re-labels new messages without
  // re-running the socket effect (its callbacks would otherwise depend on `t`).
  const tRef = useRef(t);
  tRef.current = t;
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  // The latest messages, for retryMessage to find a failed one's text.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const stateRef = useRef({
    ws: null,
    pollingTimer: null,
    reconnectTimer: null,
    reconnectAttempts: 0,
    typingClearTimer: null,
    typingDebounceTimer: null,
    mounted: true,
    lastSendAt: 0,
  });

  const appendMessages = useCallback((incomingMessages) => {
    setMessages((prevMessages) => {
      const next = dedupeMessages([
        ...prevMessages,
        ...incomingMessages.map((item) => normalizeChatMessage(item, tRef.current)).filter(Boolean),
      ]);
      return next;
    });
  }, []);

  // The server broadcasts a sent message back to its sender too, with its
  // real id. That echo takes the place of the optimistic copy; keeping both
  // showed the message twice, and a moderator delete left the copy (ENG-531).
  const receiveMessage = useCallback((payload) => {
    const incoming = normalizeChatMessage(payload, tRef.current);
    if (!incoming) return;
    setMessages((prevMessages) => {
      const pending = prevMessages.findIndex(
        (item) => String(item.id).startsWith('local-') && !item.failed && item.message === incoming.message
      );
      const rest = pending === -1 ? prevMessages : prevMessages.filter((_, index) => index !== pending);
      return dedupeMessages([...rest, incoming]);
    });
  }, []);

  const removeMessage = useCallback((messageId) => {
    setMessages((prevMessages) => prevMessages.filter((item) => String(item.id) !== String(messageId)));
  }, []);

  const fetchHistory = useCallback(async (safeBandId, persist = true) => {
    try {
      const response = await fetch(getChatHttpUrl(safeBandId), {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const incomingItems = Array.isArray(data)
        ? data
        : (Array.isArray(data.messages) ? data.messages : []);

      const normalizedMessages = incomingItems
        .map((item) => normalizeChatMessage(item, tRef.current))
        .filter(Boolean)
        .slice(-MAX_HISTORY_MESSAGES);

      // A history refresh replaces the list; keep any failed sends so they
      // stay visible until retried or removed (ENG-503).
      setMessages((prev) => [...normalizedMessages, ...prev.filter((m) => m.failed)]);

      if (persist) {
        localStorage.setItem(getStoredHistoryKey(safeBandId), JSON.stringify(normalizedMessages));
      }
    } catch (_error) {
      // Intentionally no-op: connection can be unavailable while stream is offline.
    }
  }, []);

  const sendMessage = useCallback(async (rawMessage) => {
    const safeBandId = getSafeBandId(bandId);
    const message = String(rawMessage || '').trim();

    if (!safeBandId || !message) {
      return { ok: false, reason: 'invalid' };
    }

    // Chat needs a session (ENG-503): the composer is replaced by a sign-in
    // prompt, and nothing is shown or sent for a signed-out visitor.
    if (!hasSession()) {
      return { ok: false, reason: 'signed-out' };
    }

    const state = stateRef.current;
    const now = Date.now();
    if (now - state.lastSendAt < SEND_RATE_LIMIT_MS) {
      return { ok: false, reason: 'rate-limited' };
    }
    state.lastSendAt = now;

    const optimisticMessage = normalizeChatMessage({
      id: `local-${now}`,
      username: tRef.current('chat.you'),
      message,
      timestamp: new Date().toISOString(),
    }, tRef.current);

    if (!optimisticMessage) {
      return { ok: false, reason: 'invalid' };
    }

    appendMessages([optimisticMessage]);

    // A send that didn't go through is marked failed rather than left
    // looking sent (ENG-503); the sidebar offers Retry and Remove.
    const markFailed = (reason) => {
      setMessages((prev) => prev.map((m) => (m.id === optimisticMessage.id ? { ...m, failed: true } : m)));
      return { ok: false, reason, localId: optimisticMessage.id };
    };

    try {
      if (state.ws && state.ws.readyState === WebSocket.OPEN) {
        state.ws.send(JSON.stringify({
          type: 'chat_message',
          bandId: safeBandId,
          message,
        }));
        return { ok: true };
      }

      const response = await fetch(getChatHttpUrl(safeBandId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ message }),
      });

      if (!response.ok) {
        return markFailed('http-error');
      }

      return { ok: true };
    } catch (_error) {
      return markFailed('network-error');
    }
  }, [appendMessages, bandId]);

  // Drop a failed message (Remove), or drop it and send its text again
  // (Retry): the retry is a new send, with a new optimistic message.
  const discardMessage = useCallback((localId) => {
    setMessages((prev) => prev.filter((m) => m.id !== localId));
  }, []);

  const retryMessage = useCallback(async (localId) => {
    const failed = messagesRef.current.find((m) => m.id === localId);
    if (!failed) return { ok: false, reason: 'invalid' };
    discardMessage(localId);
    stateRef.current.lastSendAt = 0;
    return sendMessage(failed.message);
  }, [discardMessage, sendMessage]);

  const deleteMessage = useCallback(async (messageId) => {
    const safeBandId = getSafeBandId(bandId);
    if (!safeBandId || !messageId) {
      return;
    }

    removeMessage(messageId);

    const state = stateRef.current;
    if (state.ws && state.ws.readyState === WebSocket.OPEN) {
      state.ws.send(JSON.stringify({
        type: 'message_deleted',
        bandId: safeBandId,
        messageId,
      }));
      return;
    }

    try {
      await fetch(`${getChatHttpUrl(safeBandId)}/${encodeURIComponent(String(messageId))}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      });
    } catch (_error) {
      // No-op. Local moderation still removes the message for current client.
    }
  }, [bandId, removeMessage]);

  const sendTypingIndicator = useCallback(() => {
    const safeBandId = getSafeBandId(bandId);
    const state = stateRef.current;
    if (!safeBandId || !state.ws || state.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    if (state.typingDebounceTimer) {
      return;
    }

    state.ws.send(JSON.stringify({
      type: 'typing',
      bandId: safeBandId,
    }));

    state.typingDebounceTimer = setTimeout(() => {
      state.typingDebounceTimer = null;
    }, TYPING_BROADCAST_DEBOUNCE_MS);
  }, [bandId]);

  useEffect(() => {
    const state = stateRef.current;
    state.mounted = true;

    const safeBandId = getSafeBandId(bandId);
    if (!safeBandId) {
      setConnectionStatus('disconnected');
      return () => {
        state.mounted = false;
      };
    }

    const storedHistory = localStorage.getItem(getStoredHistoryKey(safeBandId));
    if (storedHistory) {
      try {
        const parsedHistory = JSON.parse(storedHistory);
        const normalizedStored = Array.isArray(parsedHistory)
          ? parsedHistory.map((item) => normalizeChatMessage(item, tRef.current)).filter(Boolean)
          : [];
        setMessages(normalizedStored);
      } catch (_error) {
        // Ignore malformed local storage history.
      }
    }

    if (!enabled) {
      setConnectionStatus('offline');
      return () => {
        state.mounted = false;
      };
    }

    const clearPolling = () => {
      if (state.pollingTimer) {
        clearInterval(state.pollingTimer);
        state.pollingTimer = null;
      }
    };

    const clearReconnect = () => {
      if (state.reconnectTimer) {
        clearTimeout(state.reconnectTimer);
        state.reconnectTimer = null;
      }
    };

    const clearTypingTimer = () => {
      if (state.typingClearTimer) {
        clearTimeout(state.typingClearTimer);
        state.typingClearTimer = null;
      }
    };

    const scheduleTypingReset = () => {
      clearTypingTimer();
      state.typingClearTimer = setTimeout(() => {
        if (!state.mounted) return;
        setTypingUsers([]);
      }, 2500);
    };

    // Falling back to polling, with socket retries (DASH-19). The chat
    // socket needs a session: without one, or once the ticket endpoint has
    // refused it (401/403), the history is polled and the socket isn't tried
    // again, since asking again won't change the answer.
    let polling = false;
    let socketRefused = false;
    const socketRetry = createSocketRetry(() => connect());

    const startPolling = () => {
      if (!state.mounted) return;
      polling = true;
      setConnectionStatus('polling');
      fetchHistory(safeBandId);
      clearPolling();
      state.pollingTimer = setInterval(() => {
        fetchHistory(safeBandId);
      }, POLLING_INTERVAL_MS);
      if (!socketRefused) socketRetry.schedule();
    };

    // A socket that closed, or a connect that couldn't get a ticket.
    const handleSocketLost = () => {
      if (!state.mounted) return;
      state.ws = null;

      if (polling) {
        socketRetry.schedule();
        return;
      }

      if (state.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        startPolling();
        return;
      }

      const delay = Math.min(
        INITIAL_RECONNECT_DELAY_MS * Math.pow(2, state.reconnectAttempts),
        MAX_RECONNECT_DELAY_MS
      );
      state.reconnectAttempts += 1;
      setConnectionStatus('reconnecting');
      clearReconnect();
      state.reconnectTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      if (!state.mounted) return;
      if (!hasSession()) {
        socketRefused = true;
        startPolling();
        return;
      }
      if (!polling) setConnectionStatus(state.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');
      mintSocketTicket(SOCKET_CHAT, safeBandId).then(
        (ticket) => openSocket(ticket),
        (err) => {
          if (!state.mounted) return;
          if (err instanceof SocketTicketRefusedError) {
            socketRefused = true;
            socketRetry.cancel();
            if (!polling) startPolling();
            return;
          }
          handleSocketLost();
        }
      );
    };

    const openSocket = (ticket) => {
      if (!state.mounted) return;

      try {
        const ws = new WebSocket(getChatWsUrl(safeBandId, ticket));
        state.ws = ws;

        ws.onopen = () => {
          clearPolling();
          polling = false;
          socketRetry.reset();
          setConnectionStatus('connected');
          state.reconnectAttempts = 0;
          fetchHistory(safeBandId);
        };

        ws.onmessage = (event) => {
          if (!state.mounted) return;

          try {
            const payload = JSON.parse(event.data);

            if (payload.type === 'chat_message') {
              receiveMessage(payload);
              return;
            }

            if (payload.type === 'chat_history' && Array.isArray(payload.messages)) {
              const normalized = payload.messages
                .map((item) => normalizeChatMessage(item, tRef.current))
                .filter(Boolean)
                .slice(-MAX_HISTORY_MESSAGES);
              setMessages(normalized);
              return;
            }

            if (payload.type === 'message_deleted' && payload.messageId) {
              removeMessage(payload.messageId);
              return;
            }

            if (payload.type === 'typing' && payload.username) {
              setTypingUsers((prevUsers) => {
                if (prevUsers.includes(payload.username)) {
                  return prevUsers;
                }
                return prevUsers.concat(payload.username).slice(0, 3);
              });
              scheduleTypingReset();
            }
          } catch (_error) {
            // Ignore malformed realtime events.
          }
        };

        ws.onerror = () => {
          // handled by onclose
        };

        ws.onclose = handleSocketLost;
      } catch (_error) {
        startPolling();
      }
    };

    fetchHistory(safeBandId);
    // Open the socket on a zero-delay timer rather than synchronously so
    // StrictMode's dev-only mount → cleanup → mount pass cancels it below
    // before a socket exists; closeSocketQuietly covers a real unmount that
    // lands inside the handshake window.
    state.reconnectTimer = setTimeout(connect, 0);

    return () => {
      state.mounted = false;
      clearReconnect();
      clearPolling();
      socketRetry.cancel();
      clearTypingTimer();
      if (state.typingDebounceTimer) {
        clearTimeout(state.typingDebounceTimer);
        state.typingDebounceTimer = null;
      }
      closeSocketQuietly(state.ws);
      state.ws = null;
    };
  }, [bandId, enabled, fetchHistory, receiveMessage, removeMessage]);

  useEffect(() => {
    const safeBandId = getSafeBandId(bandId);
    if (!safeBandId) return;
    localStorage.setItem(getStoredHistoryKey(safeBandId), JSON.stringify(messages.slice(-MAX_HISTORY_MESSAGES)));
  }, [bandId, messages]);

  return {
    messages,
    typingUsers,
    connectionStatus,
    sendMessage,
    retryMessage,
    discardMessage,
    deleteMessage,
    sendTypingIndicator,
  };
};

export { normalizeChatMessage };
